import type { Language } from '~/types/shopware'
import type { LocaleCode } from '~/i18n/config'

export const useShopLanguages = () => {
  const { refreshSessionContext, sessionContext } = useSessionContext()
  const { getAvailableLanguages, changeLanguage } = useInternationalization()
  const { apiClient } = useShopwareContext()
  const { setLocale } = useI18n()
  const { data } = useNuxtData('languages')

  const languages = computed(() => data.value?.elements as Language[])

  const getLanguageByCode = async (code: LocaleCode) => {
    // First check if we have cached languages
    if (languages.value?.length) {
      const found = languages.value.find((lang) => lang.translationCode?.code.split('-')[0] === code)
      if (found) return found
    }

    // Fetch languages if not cached
    const { data } = await useAsyncData('languages', async () => {
      return await getAvailableLanguages()
    })

    if (!data.value?.elements?.length) {
      throw new Error(`No languages available`)
    }

    const found = (data.value.elements as Language[]).find(
      (lang: Language) => lang.translationCode?.code.split('-')[0] === code,
    )
    if (!found) {
      throw new Error(`Language not found: ${code}`)
    }

    return found
  }

  const changeShopLanguage = async (code: LocaleCode) => {
    try {
      const language = await getLanguageByCode(code)

      // First, update i18n locale - this must happen first to trigger menu reactivity
      await setLocale(code)

      // Then update Shopware language context
      await changeLanguage(language.id)

      // Apply language header to API client
      apiClient.defaultHeaders.apply({ 'sw-language-id': language.id })

      // Refresh session context to get updated language data
      await refreshSessionContext()

      return language.id
    } catch (error) {
      console.error('Failed to change shop language:', error)
      throw error
    }
  }

  // Update only Shopware context without changing i18n locale (to avoid URL rewriting)
  const updateShopwareContextOnly = async (code: LocaleCode) => {
    try {
      const language = await getLanguageByCode(code)

      // Update Shopware language context
      await changeLanguage(language.id)

      // Apply language header to API client
      apiClient.defaultHeaders.apply({ 'sw-language-id': language.id })

      // Refresh session context to get updated language data
      await refreshSessionContext()

      return language.id
    } catch (error) {
      console.error('Failed to update Shopware context:', error)
      throw error
    }
  }

  // Update all contexts after navigation (safe from URL rewriting)
  const updateAllContexts = async (code: LocaleCode) => {
    try {
      const language = await getLanguageByCode(code)

      // Update i18n locale (safe after navigation)
      await setLocale(code)

      // Update Shopware language context
      await changeLanguage(language.id)

      // Apply language header to API client
      apiClient.defaultHeaders.apply({ 'sw-language-id': language.id })

      // Refresh session context to get updated language data
      await refreshSessionContext()

      return language.id
    } catch (error) {
      console.error('Failed to update all contexts:', error)
      throw error
    }
  }

  return {
    getCurrentShopLanguage: () => sessionContext.value?.context?.languageIdChain?.[0],
    getLanguageByCode,
    changeShopLanguage,
    updateShopwareContextOnly,
    updateAllContexts,
    languages: readonly(languages),
  }
}
